import React, { useState, useEffect, useCallback } from 'react';
import { X, Copy, Check, Link2, UserPlus, Trash2, Search } from 'lucide-react';
import { clsx } from 'clsx';
import { useAuthStore } from '../../stores/authStore';
import { FirestoreService } from '../../services/firestore';

const ShareDocumentModal = ({
  isOpen,
  onClose,
  document: doc,
  readOnlyOnly = false,
  titleLabel = 'Document',
  createAccessTokenForUserShare = false,
}) => {
  const { user, userData } = useAuthStore();

  const [activeTab, setActiveTab] = useState('user');
  // User sharing state
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searchError, setSearchError] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [permission, setPermission] = useState('read');
  const [isSharing, setIsSharing] = useState(false);
  const [shares, setShares] = useState([]);
  const [sharesLoading, setSharesLoading] = useState(false);
  // Link sharing state
  const [linkShare, setLinkShare] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [linkCreating, setLinkCreating] = useState(false);
  const [linkDescription, setLinkDescription] = useState('');
  const [linkDescriptionSaving, setLinkDescriptionSaving] = useState(false);
  const assessment = doc?.assessment;
  const isAssessment = assessment?.enabled === true;
  const isSelfPracticeAssessment = isAssessment
    && assessment?.category === 'informal'
    && assessment?.informalType === 'self-practice';
  const isGradableAssessment = isAssessment && !isSelfPracticeAssessment;

  const loadShares = useCallback(async () => {
    if (!doc?.id) return;
    setSharesLoading(true);
    try {
      const result = await FirestoreService.getSharesForDocument(doc.id);
      setShares(result.filter(s => s.shareType === 'user'));
      const existingLink = result.find(s => s.shareType === 'link');
      setLinkShare(existingLink || null);
      setLinkDescription(existingLink?.shareDescription || '');
    } catch (err) {
      console.error('Failed to load shares:', err);
    } finally {
      setSharesLoading(false);
    }
  }, [doc?.id]);

  useEffect(() => {
    if (isOpen && doc?.id) {
      loadShares();
      setSearchEmail('');
      setSearchResult(null);
      setSearchError('');
      setLinkCopied(false);
      setPermission('read');
    }
  }, [isOpen, doc?.id, loadShares]);

  const handleSearchUser = async () => {
    const email = searchEmail.trim().toLowerCase();
    if (!email) return;
    if (email === user?.email?.toLowerCase()) {
      setSearchError('You cannot share a document with yourself.');
      setSearchResult(null);
      return;
    }
    setIsSearching(true);
    setSearchError('');
    setSearchResult(null);
    try {
      const { findRegisteredUserByEmail } = useAuthStore.getState();
      const userDoc = await findRegisteredUserByEmail(email);
      if (!userDoc) {
        setSearchError('No registered user found with that email.');
        return;
      }
      const data = userDoc.data();
      if (userData?.institutionName && data.institutionName !== userData.institutionName) {
        setSearchError('User is not in your institution.');
        return;
      }
      setSearchResult({ uid: userDoc.id, ...data });
    } catch (err) {
      console.error('User search failed:', err);
      setSearchError('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleShareWithUser = async () => {
    if (!searchResult || !doc?.id) return;
    // Check for existing share
    const existing = shares.find(s => s.recipientId === searchResult.uid);
    if (existing) {
      setSearchError('This document is already shared with this user.');
      return;
    }
    setIsSharing(true);
    try {
      const linkToken = createAccessTokenForUserShare ? crypto.randomUUID() : null;
      const shareId = await FirestoreService.createShare(doc.id, {
        documentName: doc.name || 'Untitled Document',
        documentContentType: doc.contentType || 'document',
        ownerId: user.uid,
        ownerEmail: user.email,
        ownerName: `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim(),
        shareType: 'user',
        recipientId: searchResult.uid,
        recipientEmail: searchResult.email,
        recipientName: `${searchResult.firstName || ''} ${searchResult.lastName || ''}`.trim(),
        linkToken,
        permission: readOnlyOnly ? 'read' : permission,
        institutionName: userData?.institutionName || null,
      });
      await FirestoreService.addDocumentShare(doc.id, searchResult.uid, {
        permission: readOnlyOnly ? 'read' : permission,
        ownerId: user.uid,
        shareId,
      });
      setSearchEmail('');
      setSearchResult(null);
      setSearchError('');
      await loadShares();
    } catch (err) {
      console.error('Share failed:', err);
      setSearchError('Failed to share. Please try again.');
    } finally {
      setIsSharing(false);
    }
  };

  const handleDeleteShare = async (share) => {
    try {
      await FirestoreService.deleteShare(share.id);
      if (doc?.id && share.recipientId) {
        await FirestoreService.removeDocumentShare(doc.id, share.recipientId);
      }
      setShares(prev => prev.filter(s => s.id !== share.id));
    } catch (err) {
      console.error('Failed to delete share:', err);
    }
  };

  const handleCreateLinkShare = async () => {
    if (!doc?.id) return;
    setLinkCreating(true);
    try {
      const linkToken = crypto.randomUUID();
      await FirestoreService.updateDocument(doc.id, {
        publicShareEnabled: true,
        publicShareToken: linkToken,
      });
      await FirestoreService.createShare(doc.id, {
        documentName: doc.name || 'Untitled Document',
        documentContentType: doc.contentType || 'document',
        shareDescription: linkDescription.trim(),
        allowClone: true,
        ownerId: user.uid,
        ownerEmail: user.email,
        ownerName: `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim(),
        shareType: 'link',
        recipientId: null,
        recipientEmail: null,
        recipientName: null,
        linkToken,
        permission: 'read',
        institutionName: userData?.institutionName || null,
      });
      await loadShares();
    } catch (err) {
      console.error('Failed to create link share:', err);
    } finally {
      setLinkCreating(false);
    }
  };

  const handleSaveLinkDescription = async () => {
    if (!linkShare?.id) return;
    setLinkDescriptionSaving(true);
    try {
      await FirestoreService.updateShare(linkShare.id, {
        shareDescription: linkDescription.trim(),
      });
      setLinkShare(prev => (
        prev
          ? {
              ...prev,
              shareDescription: linkDescription.trim(),
            }
          : prev
      ));
    } catch (err) {
      console.error('Failed to update link description:', err);
    } finally {
      setLinkDescriptionSaving(false);
    }
  };

  const handleRemoveLinkShare = async () => {
    if (!linkShare) return;
    try {
      await FirestoreService.deleteShare(linkShare.id);
      if (doc?.id) {
        await FirestoreService.updateDocument(doc.id, {
          publicShareEnabled: false,
          publicShareToken: null,
        });
      }
      setLinkShare(null);
      setLinkDescription('');
    } catch (err) {
      console.error('Failed to remove link share:', err);
    }
  };

  const handleCopyLink = () => {
    if (!linkShare?.linkToken) return;
    const url = `${window.location.origin}/shared/${linkShare.linkToken}`;
    navigator.clipboard.writeText(url);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Share {titleLabel.toLowerCase()} "{doc?.name || `Untitled ${titleLabel}`}"
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('user')}
            className={clsx(
              'flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors',
              activeTab === 'user'
                ? 'border-sigma-600 text-sigma-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            <UserPlus className="w-4 h-4" />
            Share with User
          </button>
          <button
            onClick={() => setActiveTab('link')}
            className={clsx(
              'flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors',
              activeTab === 'link'
                ? 'border-sigma-600 text-sigma-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            <Link2 className="w-4 h-4" />
            Link Sharing
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'user' && (
            <div className="space-y-4">
              {isGradableAssessment && (
                <div className="rounded-lg border border-sigma-200 bg-sigma-50 px-4 py-3 text-sm text-sigma-900">
                  Share this formal or class-practice assessment with registered learners. Learners open it in assessment mode, submit responses, and their work appears in the grading inbox.
                </div>
              )}

              {/* Email search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search by email
                </label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={searchEmail}
                    onChange={e => setSearchEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSearchUser()}
                    placeholder="colleague@institution.edu"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500 outline-none"
                  />
                  <button
                    onClick={handleSearchUser}
                    disabled={isSearching || !searchEmail.trim()}
                    className="px-3 py-2 bg-sigma-600 text-white rounded-lg text-sm hover:bg-sigma-700 disabled:opacity-50"
                  >
                    <Search className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {searchError && (
                <p className="text-sm text-red-600">{searchError}</p>
              )}

              {/* Search result */}
              {searchResult && (
                <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                  <div>
                    <p className="font-medium text-gray-900">
                      {searchResult.firstName} {searchResult.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{searchResult.email}</p>
                    {searchResult.role && (
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full bg-sigma-100 text-sigma-700 capitalize">
                        {searchResult.role}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {readOnlyOnly ? (
                      <span className="rounded-lg border border-gray-300 bg-gray-50 px-3 py-1.5 text-sm text-gray-600">
                        Read only
                      </span>
                    ) : (
                      <select
                        value={permission}
                        onChange={e => setPermission(e.target.value)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="read">Can view</option>
                        <option value="write">Can edit</option>
                      </select>
                    )}
                    <button
                      onClick={handleShareWithUser}
                      disabled={isSharing}
                      className="px-4 py-1.5 bg-sigma-600 text-white rounded-lg text-sm hover:bg-sigma-700 disabled:opacity-50"
                    >
                      {isSharing ? 'Sharing...' : 'Share'}
                    </button>
                  </div>
                </div>
              )}

              {/* Existing shares list */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">People with access</h3>
                {sharesLoading ? (
                  <p className="text-sm text-gray-400">Loading...</p>
                ) : shares.length === 0 ? (
                  <p className="text-sm text-gray-400">No one else has access yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {shares.map(share => (
                      <li key={share.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {share.recipientName || share.recipientEmail}
                          </p>
                          <p className="text-xs text-gray-500">
                            {share.recipientEmail} &middot; {share.permission === 'write' ? 'Can edit' : 'Can view'}
                          </p>
                        </div>
                        <button
                          onClick={() => handleDeleteShare(share)}
                          className="p-1 hover:bg-gray-200 rounded"
                          title="Remove access"
                        >
                          <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {activeTab === 'link' && (
            <div className="space-y-4">
              {isSelfPracticeAssessment ? (
                <p className="text-sm text-gray-600">
                  Anyone with the link can complete this self-practice document. Responses stay in the browser and are not submitted for grading.
                </p>
              ) : isGradableAssessment ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Link sharing lets learners open the document, then sign in to complete the formal or class-practice assessment in submission mode. Use Share with User when access should be limited to specific registered learners.
                </div>
              ) : (
                <p className="text-sm text-gray-600">
                  Anyone with the link can view this document (read-only) and clone their own copy after signing in.
                </p>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Share description
                </label>
                <textarea
                  value={linkDescription}
                  onChange={e => setLinkDescription(e.target.value)}
                  rows={3}
                  maxLength={220}
                  placeholder="Add a short description for social sharing previews."
                  className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 outline-none focus:border-sigma-500 focus:ring-2 focus:ring-sigma-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  This description is used for social platform previews when someone shares the link.
                </p>
              </div>

              {linkShare ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={`${window.location.origin}/shared/${linkShare.linkToken}`}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-700"
                    />
                    <button
                      onClick={handleCopyLink}
                      className="px-3 py-2 bg-sigma-600 text-white rounded-lg text-sm hover:bg-sigma-700 flex items-center gap-1"
                    >
                      {linkCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {linkCopied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  <button
                    onClick={handleSaveLinkDescription}
                    disabled={linkDescriptionSaving}
                    className="px-4 py-2 bg-sigma-600 text-white rounded-lg text-sm hover:bg-sigma-700 disabled:opacity-50"
                  >
                    {linkDescriptionSaving ? 'Saving...' : 'Save description'}
                  </button>
                  <button
                    onClick={handleRemoveLinkShare}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Disable link sharing
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleCreateLinkShare}
                  disabled={linkCreating}
                  className="px-4 py-2 bg-sigma-600 text-white rounded-lg text-sm hover:bg-sigma-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Link2 className="w-4 h-4" />
                  {linkCreating ? 'Creating...' : 'Enable link sharing'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareDocumentModal;
